import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Search, X, Phone, Mail, Loader2, Plus, Trash2, Check,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown,
  Calendar, PhoneCall, Building2,
  Tag, Table as TableIcon, CalendarDays, SlidersHorizontal,
  TrendingUp, TrendingDown, Minus, Truck,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { smoothPath } from '../lib/chart.js'
import {
  CHANNELS, CHANNEL_DOT, STAGES, STAGE_DOT, STAGE_BADGE, CARD, CARD_BASE,
  CompanyCombobox, SingleChips, Field, Toast,
  FilterButton, ChipPanel, DatePanel, ActiveChip, ViewSwitcher, ConfirmDialog, CreatedByChip,
} from '../components/ui.jsx'

const NEW_FU = Object.freeze({ __new: true })

const VIEWS = [
  { v: 'table', label: 'Table',   icon: TableIcon    },
  { v: 'date',  label: 'By Date', icon: CalendarDays },
]

export default function FollowUps() {
  const [q, setQ]               = useState('')
  const [stage, setStage]       = useState([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo]     = useState('')
  const [page, setPage]         = useState(1)
  const [pageSize]              = useState(50)
  const [viewMode, setViewMode] = useState('table')
  const [sort, setSort]         = useState('date')      // 'date' | 'recall_at' | 'created_at'
  const [dir, setDir]           = useState('desc')      // default desc
  const [onlyWithOps, setOnlyWithOps] = useState(false) // show only follow-ups whose company has operations

  // Activity-summary chart (hero + monthly trend) — same shape as Leads page
  const [stats, setStats]             = useState(null)
  const [statsPeriod, setStatsPeriod] = useState(7)
  useEffect(() => { api.followUps.stats(statsPeriod).then(setStats).catch(() => {}) }, [statsPeriod])

  const [data, setData]   = useState(null)
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null)
  const [lastViewedId, setLastViewedId] = useState(() => {
    const v = parseInt(sessionStorage.getItem('lastViewedFollowUpId') || '', 10)
    return Number.isFinite(v) ? v : null
  })
  function openFu(row) {
    if (row?.id) { sessionStorage.setItem('lastViewedFollowUpId', String(row.id)); setLastViewedId(row.id) }
    setEditing(row)
  }
  const [toast, setToast] = useState(null)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())

  useEffect(() => { setPage(1); setCollapsedGroups(new Set()) },
    [q, stage, dateFrom, dateTo, viewMode, sort, dir, onlyWithOps])

  const refresh = () => {
    setError(null)
    api.followUps.list({
      q,
      outcome: stage,
      date_from: dateFrom,
      date_to: dateTo,
      sort, dir,
      only_with_ops: onlyWithOps ? 1 : '',
      page, page_size: pageSize,
    })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => { api.followUps.stats(statsPeriod).then(setStats).catch(() => {}) })
  }
  useEffect(refresh, [q, stage, dateFrom, dateTo, sort, dir, onlyWithOps, page, pageSize])

  const handleSort = (field) => {
    if (sort === field) setDir(dir === 'asc' ? 'desc' : 'asc')
    else { setSort(field); setDir('desc') }
  }

  const activeCount = (stage.length ? 1 : 0) + (dateFrom || dateTo ? 1 : 0) + (onlyWithOps ? 1 : 0)
  const clearAll = () => { setQ(''); setStage([]); setDateFrom(''); setDateTo(''); setOnlyWithOps(false) }

  const total      = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const fromIdx    = total === 0 ? 0 : (page - 1) * pageSize + 1
  const toIdx      = Math.min(total, page * pageSize)

  const groups = useMemo(() => {
    if (!data || viewMode !== 'date') return []
    return groupByDate(data.rows)
  }, [data, viewMode])

  return (
    <div className="px-4 py-5 md:px-10 md:py-8 max-w-[1400px] mx-auto">
      <header className="mb-5 md:mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
        <div className="flex items-start gap-3">
          <span aria-hidden className="hidden md:block mt-1.5 w-1 h-9 rounded-full bg-gradient-to-b from-amber-400 to-orange-500" />
          <div>
            <h1 className="text-[24px] md:text-[28px] font-bold tracking-tight text-zinc-900 leading-tight">Follow-ups</h1>
            <p className="text-[13px] md:text-[13.5px] text-zinc-500 mt-1">Track every call, email, LINE chat, and visit.</p>
          </div>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2.5">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(NEW_FU)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 shadow-sm shadow-amber-500/25 transition-colors"
            >
              <Plus size={14} strokeWidth={2.5} />
              New follow-up
            </button>
            <ViewSwitcher value={viewMode} onChange={setViewMode} views={VIEWS} />
          </div>
          <div className="text-[12.5px] text-zinc-500 tabular-nums inline-flex items-baseline gap-1.5">
            {data ? (
              <>
                <span className="text-[15px] font-semibold text-zinc-900">{total.toLocaleString()}</span>
                <span>follow-ups</span>
                <span className="text-zinc-300 mx-1">·</span>
                <span>showing</span>
                <span className="font-medium text-zinc-700">{fromIdx.toLocaleString()}–{toIdx.toLocaleString()}</span>
              </>
            ) : 'Loading…'}
          </div>
        </div>
      </header>

      {/* Activity summary — same hero + monthly trend pattern as Leads */}
      <FollowUpStats stats={stats} period={statsPeriod} onPeriodChange={setStatsPeriod} />

      {/* Toolbar */}
      <div className={`${CARD_BASE} relative z-10`}>
        <div className="px-3 py-2.5 flex flex-col gap-2 md:flex-row md:items-center md:gap-2">
          <div className="relative w-full md:flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหา (บริษัท · note)"
              className="w-full pl-9 pr-3 py-2 bg-transparent text-[13.5px] placeholder:text-zinc-400 focus:outline-none"
            />
          </div>

          <div className="hidden md:block h-6 w-px bg-zinc-200" />

          <div className="flex items-center gap-2 flex-wrap">
            <FilterButton icon={Tag} label="Outcome" count={stage.length}>
              <ChipPanel options={STAGES} value={stage} onChange={setStage} dotMap={STAGE_DOT} />
            </FilterButton>
            <FilterButton icon={Calendar} label="Date" count={dateFrom || dateTo ? 1 : 0}>
              <DatePanel from={dateFrom} to={dateTo}
                onChange={(f, t) => { setDateFrom(f); setDateTo(t) }} />
            </FilterButton>

            <button
              type="button"
              onClick={() => setOnlyWithOps((v) => !v)}
              className={
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ring-1 ' +
                (onlyWithOps
                  ? 'bg-amber-50 text-amber-800 ring-amber-200'
                  : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50')
              }
              title="Show only follow-ups whose company has at least one operation record"
            >
              <Truck size={13} strokeWidth={2.25} />
              Has operations
            </button>

            {activeCount > 0 && (
              <button onClick={clearAll}
                className="px-2.5 py-1.5 rounded-md text-[12px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50">
                Clear
              </button>
            )}
          </div>
        </div>

        {activeCount > 0 && (
          <div className="px-3 pb-3 -mt-1 flex flex-wrap items-center gap-1.5">
            {stage.map((s) => (
              <ActiveChip key={`st-${s}`} icon={Tag} label="Outcome" value={s}
                onRemove={() => setStage(stage.filter((x) => x !== s))} />
            ))}
            {(dateFrom || dateTo) && (
              <ActiveChip icon={Calendar} label="Date"
                value={`${dateFrom || '…'} → ${dateTo || '…'}`}
                onRemove={() => { setDateFrom(''); setDateTo('') }} />
            )}
            {onlyWithOps && (
              <ActiveChip icon={Truck} label="Filter" value="Has operations"
                onRemove={() => setOnlyWithOps(false)} />
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 px-4 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px]">{error}</div>
      )}

      {/* List area */}
      <div className="mt-4">
        {viewMode === 'table'
          ? <FlatTable data={data} onRowClick={openFu} lastViewedId={lastViewedId} activeCount={activeCount} clearAll={clearAll}
                       sort={sort} dir={dir} onSort={handleSort} />
          : <GroupedTable data={data} groups={groups} onRowClick={openFu} lastViewedId={lastViewedId}
                          collapsedGroups={collapsedGroups} setCollapsedGroups={setCollapsedGroups}
                          activeCount={activeCount} clearAll={clearAll}
                          sort={sort} dir={dir} onSort={handleSort} />}
      </div>

      {/* Pagination */}
      {data && total > 0 && (
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12.5px] text-zinc-600">
          <div className="tabular-nums text-center md:text-left">
            {fromIdx.toLocaleString()}–{toIdx.toLocaleString()} of {total.toLocaleString()}
          </div>
          <div className="flex justify-center gap-0.5">
            <PagerBtn disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={13} /></PagerBtn>
            <span className="px-2 py-1 text-[12px] tabular-nums">{page} / {totalPages}</span>
            <PagerBtn disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={13} /></PagerBtn>
          </div>
        </div>
      )}

      <FollowUpDrawer
        followUp={editing}
        onClose={() => setEditing(null)}
        onSaved={(isNew) => { setEditing(null); refresh(); setToast({ id: Date.now(), message: isNew ? 'Follow-up created' : 'Changes saved' }) }}
        onDeleted={() => { setEditing(null); refresh(); setToast({ id: Date.now(), message: 'Follow-up deleted' }) }}
        onAddAnother={(company) => {
          // Replace the current drawer with a fresh "new follow-up" pre-seeded with the same company.
          setEditing({ __new: true, company_id: company.id, company_name: company.name })
        }}
      />
      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  )
}

/* ──────────────────────────────── flat table view ──────────────────────────────── */

function ColsThead({ sort, dir, onSort }) {
  return (
    <thead>
      <tr className="border-b border-zinc-100">
        <SortHeader field="date"       label="Date"           sort={sort} dir={dir} onClick={onSort} className="px-3 md:px-5" />
        <th className="px-3 md:px-5 py-3 text-left text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Anchor / Note</th>
        <th className="hidden md:table-cell px-5 py-3 text-left text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Outcome</th>
        <SortHeader field="recall_at"  label="Next recall"    sort={sort} dir={dir} onClick={onSort} className="hidden md:table-cell px-5" />
      </tr>
    </thead>
  )
}

function colGroup() {
  return (
    <colgroup>
      <col className="w-[110px] md:w-[140px]" />
      <col />
      <col className="hidden md:table-column md:w-[170px]" />
      <col className="hidden md:table-column md:w-[120px]" />
    </colgroup>
  )
}

/* Reusable sortable column header — same visual language as Leads.jsx */
function SortHeader({ field, label, sort, dir, onClick, className = '' }) {
  const active = sort === field
  const Arrow = !active ? ChevronsUpDown : (dir === 'asc' ? ChevronUp : ChevronDown)
  return (
    <th className={`py-3 text-left ${className}`}>
      <button
        type="button"
        onClick={() => onClick(field)}
        className={
          'group inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-medium rounded transition-colors ' +
          (active ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-900')
        }
      >
        <span>{label}</span>
        <Arrow size={12} strokeWidth={2.25}
          className={active ? 'text-amber-600' : 'text-zinc-400 group-hover:text-zinc-600'} />
      </button>
    </th>
  )
}

function FlatTable({ data, onRowClick, lastViewedId, activeCount, clearAll, sort, dir, onSort }) {
  return (
    <div className={CARD}>
      <table className="w-full table-fixed text-[13px]">
        {colGroup()}
        <ColsThead sort={sort} dir={dir} onSort={onSort} />
        <tbody>
          {!data && <tr><td colSpan={4} className="px-5 py-12 text-center text-zinc-400">Loading…</td></tr>}
          {data && data.rows.length === 0 && <EmptyRow activeCount={activeCount} clearAll={clearAll} />}
          {data && data.rows.map((f) => <FuRow key={f.id} f={f} onClick={() => onRowClick(f)} isLastViewed={f.id === lastViewedId} />)}
        </tbody>
      </table>
    </div>
  )
}

function GroupedTable({ data, groups, onRowClick, lastViewedId, collapsedGroups, setCollapsedGroups, activeCount, clearAll, sort, dir, onSort }) {
  const toggle = (key) => {
    const next = new Set(collapsedGroups)
    if (next.has(key)) next.delete(key); else next.add(key)
    setCollapsedGroups(next)
  }
  return (
    <div className="space-y-4">
      {/* Shared column-header card */}
      <div className={CARD}>
        <table className="w-full table-fixed text-[13px]">
          {colGroup()}
          <ColsThead sort={sort} dir={dir} onSort={onSort} />
          <tbody><tr><td colSpan={4} className="h-0 p-0 border-0" /></tr></tbody>
        </table>
      </div>

      {!data && (
        <div className={CARD}><table className="w-full"><tbody>
          <tr><td colSpan={4} className="px-5 py-12 text-center text-zinc-400">Loading…</td></tr>
        </tbody></table></div>
      )}
      {data && groups.length === 0 && (
        <div className={CARD}><table className="w-full"><tbody>
          <EmptyRow activeCount={activeCount} clearAll={clearAll} />
        </tbody></table></div>
      )}
      {data && groups.map((g) => {
        const collapsed = collapsedGroups.has(g.key)
        return (
          <div key={g.key} className={CARD}>
            <button
              onClick={() => toggle(g.key)}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-zinc-50/60 border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
            >
              <ChevronDown size={14} strokeWidth={2.5}
                className={'text-zinc-500 transition-transform ' + (collapsed ? '-rotate-90' : '')} />
              <span className="text-[13px] font-semibold text-zinc-800 tabular-nums">{g.label}</span>
              <span className="text-[11.5px] text-zinc-500 tabular-nums ml-1">
                · {g.rows.length} {g.rows.length === 1 ? 'record' : 'records'}
              </span>
            </button>
            {!collapsed && (
              <table className="w-full table-fixed text-[13px]">
                {colGroup()}
                <tbody>
                  {g.rows.map((f) => <FuRow key={f.id} f={f} onClick={() => onRowClick(f)} isLastViewed={f.id === lastViewedId} />)}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
    </div>
  )
}

function FuRow({ f, onClick, isLastViewed }) {
  return (
    <tr onClick={onClick}
      className={
        'border-b border-zinc-100 last:border-b-0 transition-colors cursor-pointer ' +
        (isLastViewed
          ? 'bg-amber-50 hover:bg-amber-100/70 ring-1 ring-inset ring-amber-300/40'
          : 'hover:bg-amber-50/30')
      }>
      <td className="px-3 md:px-5 py-2.5 align-top">
        <div className="text-[12.5px] font-medium text-zinc-700 tabular-nums">{fmtDate(f.date)}</div>
        <div className="text-[11px] text-zinc-400 tabular-nums">{fmtTime(f.date)}</div>
      </td>
      <td className="px-3 md:px-5 py-2.5 align-top">
        <AnchorName company={f.company_name} lead={f.lead_name} />
        <div className="mt-0.5 text-[12px] text-zinc-500 line-clamp-2">
          {f.note || <span className="text-zinc-300">(no note)</span>}
        </div>
        {f.outcome_stage && (
          <div className="md:hidden mt-1 flex items-center gap-2 text-[11px]">
            <StageChip stage={f.outcome_stage} />
          </div>
        )}
      </td>
      <td className="hidden md:table-cell px-5 py-2.5 align-top">
        {f.outcome_stage ? <StageChip stage={f.outcome_stage} /> : <span className="text-zinc-300">—</span>}
      </td>
      <td className="hidden md:table-cell px-5 py-2.5 align-top">
        {f.recall_at ? (
          <RecallCell ts={f.recall_at} />
        ) : (
          <span className="text-zinc-300">—</span>
        )}
      </td>
    </tr>
  )
}

/* Next-recall cell — date on top, relative "in N days" / "today" / "Xd ago" hint below.
   Past-due dates render in amber to match the brand. */
function RecallCell({ ts }) {
  const now      = Date.now() / 1000
  const days     = Math.floor((ts - now) / 86400)
  const overdue  = ts < now
  const relative = overdue
    ? (days <= -1 ? `${-days}d ago` : 'today')
    : (days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days}d`)
  return (
    <>
      <div className={`text-[12.5px] font-medium tabular-nums ${overdue ? 'text-amber-700' : 'text-zinc-700'}`}>
        {fmtDate(ts)}
      </div>
      <div className={`text-[11px] tabular-nums ${overdue ? 'text-amber-600' : 'text-zinc-400'}`}>
        {relative}
      </div>
    </>
  )
}

function EmptyRow({ activeCount, clearAll }) {
  return (
    <tr><td colSpan={4} className="px-5 py-12 text-center">
      <SlidersHorizontal size={20} className="mx-auto text-zinc-300" />
      <div className="mt-2 text-[13px] text-zinc-500">No follow-ups match these filters.</div>
      {activeCount > 0 && (
        <button onClick={clearAll}
          className="mt-2 text-[12.5px] text-amber-600 hover:text-amber-700 font-medium">
          Clear filters
        </button>
      )}
    </td></tr>
  )
}

function AnchorName({ company, lead }) {
  if (company) {
    return <div className="font-medium text-zinc-900 truncate" title={company}>{company}</div>
  }
  if (lead) {
    return (
      <div className="font-medium text-zinc-900 truncate" title={lead}>
        <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold tracking-wider bg-amber-50 text-amber-700 ring-1 ring-amber-200/70 align-middle">Lead</span>
        {lead}
      </div>
    )
  }
  return <div className="text-zinc-300 italic">— no anchor —</div>
}

function StageChip({ stage }) {
  const cls = STAGE_BADGE[stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'
  return (
    <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded ring-1 text-[11px] font-medium ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STAGE_DOT[stage] || 'bg-zinc-400'}`} />
      {stage}
    </span>
  )
}

function PagerBtn({ children, disabled, onClick }) {
  return (
    <button disabled={disabled} onClick={onClick}
      className={'min-w-[26px] h-7 px-1.5 rounded-md text-[12px] font-medium flex items-center justify-center ' +
        (disabled ? 'text-zinc-300 cursor-not-allowed' : 'text-zinc-600 hover:bg-zinc-100')}>
      {children}
    </button>
  )
}

/* ──────────────────────────────── helpers ──────────────────────────────── */

function pad(n) { return String(n).padStart(2, '0') }
function fmtDate(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtTime(unix) {
  if (!unix) return ''
  return new Date(unix * 1000).toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}
function groupByDate(rows) {
  const out = []
  for (const row of rows) {
    const ts  = row.date ? row.date * 1000 : 0
    const d   = new Date(ts)
    const key = ts ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : 'no-date'
    const label = ts
      ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
      : 'No date'
    const last = out[out.length - 1]
    if (last && last.key === key) last.rows.push(row)
    else out.push({ key, label, rows: [row] })
  }
  return out
}

/* ──────────────────────────────── Follow-up drawer ──────────────────────────────── */

const HISTORY_PER_PAGE = 5

export function FollowUpDrawer({ followUp, onClose, onSaved, onDeleted, onAddAnother, lockedCompany, lockedLead, zClass = 'z-[45]' }) {
  const open  = !!followUp
  // Drawer maintains its own "currently-viewed" follow-up so clicking a history
  // item swaps the active record without closing/re-opening. Parent re-opens it
  // by passing a new `followUp` prop, which resets both `current` and the stack.
  const [current, setCurrent] = useState(followUp)
  const [navStack, setNavStack] = useState([])     // history of previously-viewed follow-ups (for Back)
  const [navLoading, setNavLoading] = useState(false)

  const isNew = current?.__new === true
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [err, setErr] = useState(null)

  // All other follow-ups under the same company — read-only history, paginated.
  const [history, setHistory] = useState(null)   // null = loading, [] = none, [...] = items
  const [historyPage, setHistoryPage] = useState(1)

  // Parent forced a new follow-up → reset everything.
  useEffect(() => {
    setCurrent(followUp)
    setNavStack([])
  }, [followUp?.id, followUp?.__new])

  useEffect(() => {
    if (current) {
      const now = Math.floor(Date.now() / 1000)
      setForm({
        company:        lockedCompany || (current.company_id ? { id: current.company_id, name: current.company_name } : null),
        lead:           lockedLead || (current.lead_id ? { id: current.lead_id, name: current.lead_name } : null),
        date:           current.date  || now,
        channel:        current.channel || '',
        note:           current.note || '',
        outcome_stage:  current.outcome_stage || '',
        recall_at:      current.recall_at || now,
      })
      setErr(null)
      setConfirmOpen(false)
    }
  }, [current?.id, current?.__new])

  // Fetch the company's full follow-up history whenever the picked company changes.
  // We piggyback on /api/companies/:id which already returns follow_ups DESC by date.
  useEffect(() => {
    const cid = form?.company?.id
    setHistoryPage(1)
    if (!cid) { setHistory(null); return }
    let cancelled = false
    setHistory(null)
    api.companies.get(cid)
      .then((d) => { if (!cancelled) setHistory(Array.isArray(d?.follow_ups) ? d.follow_ups : []) })
      .catch(() => { if (!cancelled) setHistory([]) })
    return () => { cancelled = true }
  }, [form?.company?.id])

  // Exclude whichever row we're currently editing so it doesn't show twice.
  const otherHistory = (history || []).filter((f) => !current?.id || f.id !== current.id)
  const totalHistoryPages = Math.max(1, Math.ceil(otherHistory.length / HISTORY_PER_PAGE))
  const historyPageItems  = otherHistory.slice((historyPage - 1) * HISTORY_PER_PAGE, historyPage * HISTORY_PER_PAGE)

  /** Jump to another follow-up under the same company. Pushes the active row
   *  onto the back-stack so the user can return via the Back button. */
  async function navigateTo(id) {
    if (!id || id === current?.id) return
    setNavLoading(true); setErr(null)
    try {
      const next = await api.followUps.get(id)
      setNavStack((s) => [...s, current])
      setCurrent(next)
    } catch (e) {
      setErr(e?.message || 'ไม่สามารถโหลดรายการได้')
    } finally {
      setNavLoading(false)
    }
  }
  function navigateBack() {
    setNavStack((s) => {
      if (!s.length) return s
      const prev = s[s.length - 1]
      setCurrent(prev)
      return s.slice(0, -1)
    })
  }

  useEffect(() => {
    if (!open) return
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  const save = async () => {
    setErr(null)
    if (!form.company?.id && !form.lead?.id) {
      setErr('Please select a company or a lead.'); return
    }
    if (!form.date) { setErr('Please pick a date.'); return }

    const payload = {
      lead_id:       form.lead?.id    || null,
      company_id:    form.company?.id || null,
      date:          form.date,
      channel:       form.channel || null,
      note:          form.note?.trim() || null,
      outcome_stage: form.outcome_stage || null,
      recall_at:     form.recall_at || Math.floor(Date.now() / 1000),
    }

    setSaving(true)
    try {
      // Stamp the activity time at save time so it always reflects when the
      // staff actually finished writing the entry. The Date field is hidden;
      // for new rows we always use "now"; for existing rows we keep the
      // original timestamp untouched.
      if (isNew) {
        payload.date = Math.floor(Date.now() / 1000)
        await api.followUps.create(payload)
      } else {
        await api.followUps.update(current.id, payload)
      }
      onSaved(isNew)
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const doDelete = async () => {
    setErr(null); setDeleting(true)
    try { await api.followUps.remove(current.id); onDeleted?.(current.id) }
    catch (e) { setErr(e.message) }
    finally { setDeleting(false); setConfirmOpen(false) }
  }

  if (!open || !form) return null

  return (
    <div className={'fixed inset-0 ' + zClass}>
      <div onClick={onClose} className="absolute inset-0 bg-zinc-900/20 backdrop-blur-[1px]" />
      <aside className="absolute right-0 top-0 h-full w-[460px] max-w-[100vw] bg-white shadow-2xl ring-1 ring-zinc-200 flex flex-col">
        <header className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Back button — appears when the user has navigated into a sibling
                follow-up via the "Other follow-ups" list. */}
            {navStack.length > 0 && (
              <button
                type="button"
                onClick={navigateBack}
                disabled={navLoading}
                title={`Back (${navStack.length} step${navStack.length > 1 ? 's' : ''})`}
                className="shrink-0 w-8 h-8 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 inline-flex items-center justify-center disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </button>
            )}
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-medium text-amber-600">
                {isNew ? 'New follow-up' : 'Edit follow-up'}
                {navStack.length > 0 && (
                  <span className="ml-1.5 text-zinc-400 normal-case tracking-normal font-normal">
                    · viewing other ({navStack.length} back)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-[15px] font-semibold text-zinc-900 truncate">
                  {form.lead?.name
                    ? <>Lead: {form.lead.name}</>
                    : form.company?.name
                      ? form.company.name
                      : <span className="text-zinc-400 font-normal italic">Select a company or lead</span>}
                </h2>
                {!isNew && current?.created_at && (
                  <CreatedByChip
                    createdAtUnix={current.created_at}
                    createdById={current.created_by_xano_id}
                    fallbackCode={current.created_by_code}
                  />
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 flex items-center justify-center">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {lockedLead && (
            <div className="px-3 py-2 rounded-lg bg-amber-50 ring-1 ring-amber-200/70 text-[12.5px]">
              <span className="text-amber-700 font-medium uppercase tracking-wider text-[10.5px]">Lead</span>
              <span className="block mt-0.5 text-zinc-900 font-medium">{form.lead?.name}</span>
            </div>
          )}
          {!lockedCompany && !lockedLead && (
            <Field label="Company" icon={Building2} hintInline="or pick from a lead">
              <CompanyCombobox value={form.company} onChange={(c) => setForm({ ...form, company: c })} />
            </Field>
          )}

          {/* Date input intentionally hidden — auto-set to "now" on save.
              Stamped after the row is created so the time always reflects when
              the staff actually finished writing the follow-up. */}
          <Field label="Recall on" hintInline="next contact">
            <input
              type="date"
              value={unixToDate(form.recall_at)}
              min={todayStr()}
              onChange={(e) => setForm({ ...form, recall_at: dateToUnix(e.target.value) })}
              className="w-full sm:max-w-xs px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none"
            />
          </Field>

          <Field label="Outcome stage">
            <SingleChips options={STAGES} value={form.outcome_stage} dotMap={STAGE_DOT} allowClear
              onChange={(v) => setForm({ ...form, outcome_stage: v })} />
          </Field>

          <Field label="Note">
            <textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none resize-y" />
          </Field>

          {/* Other follow-ups under the same company — read-only, paginated, clickable */}
          {form.company?.id && (
            <div className="pt-3 border-t border-zinc-100">
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">
                  Other follow-ups
                  {history != null && (
                    <span className="ml-1.5 text-zinc-400 normal-case tracking-normal font-normal">
                      ({otherHistory.length})
                    </span>
                  )}
                </div>
                {/* New-follow-up button moved up here (was in the footer) so it sits
                    contextually next to the history list it adds to. */}
                {!isNew && current?.id && onAddAnother && (
                  <button
                    type="button"
                    onClick={() => onAddAnother({ id: form.company.id, name: form.company.name })}
                    disabled={saving || deleting}
                    title="Log another follow-up for this company"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11.5px] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                  >
                    <Plus size={11} strokeWidth={2.5} />
                    New follow-up
                  </button>
                )}
              </div>
              {history == null ? (
                <div className="text-[12.5px] text-zinc-400 py-3 text-center">Loading…</div>
              ) : otherHistory.length === 0 ? (
                <div className="text-[12.5px] text-zinc-400 py-3 text-center">
                  No other follow-ups for this company yet.
                </div>
              ) : (
                <>
                  <ul className="space-y-1">
                    {historyPageItems.map((f) => (
                      <HistoryItem key={f.id} f={f} disabled={navLoading} onClick={() => navigateTo(f.id)} />
                    ))}
                  </ul>
                  {totalHistoryPages > 1 && (
                    <div className="flex items-center justify-between mt-2.5 text-[11.5px]">
                      <button
                        type="button"
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                        disabled={historyPage === 1}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft size={12} /> Prev
                      </button>
                      <span className="text-zinc-500 tabular-nums">
                        Page {historyPage} of {totalHistoryPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
                        disabled={historyPage === totalHistoryPages}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Next <ChevronRight size={12} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {err && <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[12.5px]">{err}</div>}
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-2 bg-zinc-50/60">
          <div className="flex items-center gap-2">
            {!isNew && (
              <button onClick={() => setConfirmOpen(true)} disabled={deleting || saving}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50">
                {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} strokeWidth={2.25} />}
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-[13px] font-medium text-zinc-700 hover:bg-zinc-100">
              Cancel
            </button>
            <button onClick={save} disabled={saving || deleting}
              className="px-3.5 py-1.5 rounded-lg text-[13px] font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm shadow-amber-500/20">
              {saving && <Loader2 size={13} className="animate-spin" />}
              {isNew ? 'Create' : 'Save changes'}
            </button>
          </div>
        </footer>
      </aside>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete follow-up"
        message="This will permanently remove this follow-up. The action can't be undone."
        confirmLabel="Delete"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  )
}

/* ──────────────────────────────── Activity summary ────────────────────────────────
 *  Mirrors the Lead page hero + monthly chart so the two pages feel of a kind.
 *  Counts every follow_up.date (not created_at) — what users care about is
 *  *when interactions happened*, not when rows were typed.
 * ──────────────────────────────────────────────────────────────────────────────── */

const STAT_PERIODS = [
  { v: 7,  label: '7d' },
  { v: 14, label: '14d' },
  { v: 30, label: '1mo' },
]
const STAT_PERIOD_LONG = { 7: 'Last 7 days', 14: 'Last 14 days', 30: 'Last 1 month' }

function FollowUpStats({ stats, period, onPeriodChange }) {
  return (
    <section className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <FollowUpHero stats={stats?.hero} period={period} onPeriodChange={onPeriodChange} />
      <FollowUpTrend monthly={stats?.monthly} />
    </section>
  )
}

function FollowUpHero({ stats, period, onPeriodChange }) {
  const loading = !stats
  const count = stats?.count ?? 0
  const prev  = stats?.prev  ?? 0
  const delta = stats?.delta_pct

  let trendIcon, trendLabel, trendCls
  if (delta == null) {
    trendIcon = TrendingUp
    trendLabel = count === 0 ? 'no prior data' : `+${count} new`
    trendCls = count > 0 ? 'text-emerald-700 bg-emerald-50 ring-emerald-200/70' : 'text-zinc-500 bg-zinc-50 ring-zinc-200/70'
  } else if (delta > 0) {
    trendIcon = TrendingUp;   trendLabel = `+${delta}%`;  trendCls = 'text-emerald-700 bg-emerald-50 ring-emerald-200/70'
  } else if (delta < 0) {
    trendIcon = TrendingDown; trendLabel = `${delta}%`;   trendCls = 'text-rose-700 bg-rose-50 ring-rose-200/70'
  } else {
    trendIcon = Minus;        trendLabel = 'no change';   trendCls = 'text-zinc-600 bg-zinc-50 ring-zinc-200/70'
  }
  const TrendIcon = trendIcon

  return (
    <div className={`${CARD} p-5`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">{STAT_PERIOD_LONG[period]}</div>
        <div className="inline-flex bg-zinc-100/80 rounded-md p-0.5 ring-1 ring-zinc-200/60">
          {STAT_PERIODS.map((p) => {
            const on = p.v === period
            return (
              <button
                key={p.v}
                onClick={() => onPeriodChange(p.v)}
                className={'px-2 py-0.5 rounded text-[10.5px] font-medium transition-colors ' +
                  (on ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-900')}
              >
                {p.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-[34px] font-bold tabular-nums text-zinc-900 leading-none">
          {loading ? '–' : count.toLocaleString()}
        </div>
        <div className="text-[12px] text-zinc-500">follow-ups logged</div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 text-[11.5px] font-medium ${trendCls}`}>
          <TrendIcon size={12} strokeWidth={2.5} />
          {trendLabel}
        </span>
        <span className="text-[11.5px] text-zinc-400 tabular-nums">vs prior {period}d ({loading ? '–' : prev.toLocaleString()})</span>
      </div>
      <p className="mt-3 text-[11px] text-zinc-400">Counted by interaction date.</p>
    </div>
  )
}

function FollowUpTrend({ monthly }) {
  const loading = !monthly
  const data = monthly || []
  const total = data.reduce((acc, m) => acc + m.count, 0)
  const [hoverIdx, setHoverIdx] = useState(null)

  const W = 540, H = 100
  const padT = 6, padB = 6
  const inner = { x: 0, y: padT, w: W, h: H - padT - padB }
  const max = Math.max(1, ...data.map((m) => m.count))
  const stepX = data.length > 1 ? inner.w / (data.length - 1) : 0
  const pts = data.map((m, i) => {
    const x = i * stepX
    const y = inner.y + inner.h - (m.count / max) * inner.h
    return { x, y, normX: data.length > 1 ? i / (data.length - 1) : 0.5, normY: m.count / max, m }
  })
  const linePath = smoothPath(pts)
  const areaPath = pts.length
    ? `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${inner.y + inner.h} L 0 ${inner.y + inner.h} Z`
    : ''

  const labelIdx = data.length <= 1 ? [0] : [0, Math.floor(data.length / 2), data.length - 1]
  const fmtMonth = (s) => {
    if (!s) return ''
    const [y, m] = s.split('-').map(Number)
    const d = new Date(y, m - 1, 1)
    return d.toLocaleDateString('en-US', { month: 'short' }) + ' ' + String(y).slice(2)
  }

  const handleMove = (e) => {
    if (!pts.length) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const ratio = Math.max(0, Math.min(1, x / rect.width))
    const idx = Math.round(ratio * (pts.length - 1))
    setHoverIdx(idx)
  }

  return (
    <div className={`${CARD} p-5 md:col-span-2 relative`}>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-500">Follow-ups per month</div>
          <p className="text-[12px] text-zinc-400 mt-0.5">Last 12 months · by interaction date</p>
        </div>
        <div className="text-[12px] text-zinc-500 tabular-nums">
          {loading ? '–' : <><span className="text-zinc-900 font-semibold text-[14px]">{total.toLocaleString()}</span> total</>}
        </div>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="h-[120px] flex items-center justify-center text-zinc-300 text-[12px]">Loading…</div>
        ) : (
          <div className="relative h-[120px] cursor-crosshair"
               onMouseMove={handleMove}
               onMouseLeave={() => setHoverIdx(null)}>
            <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="ttFuFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#f59e0b" stopOpacity="0.30" />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              {[0.25, 0.5, 0.75, 1].map((t) => (
                <line key={t} x1={0} x2={W}
                  y1={inner.y + inner.h - inner.h * t}
                  y2={inner.y + inner.h - inner.h * t}
                  stroke="#f4f4f5" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              ))}
              {pts.length > 0 && <path d={areaPath} fill="url(#ttFuFill)" />}
              {pts.length > 0 && (
                <path d={linePath} fill="none" stroke="#f59e0b" strokeWidth="2"
                  strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              )}
            </svg>

            {hoverIdx != null && pts[hoverIdx] && (
              <div aria-hidden
                className="absolute top-0 bottom-0 w-px bg-zinc-300/80 pointer-events-none"
                style={{ left: `${pts[hoverIdx].normX * 100}%` }} />
            )}

            {pts.map((p, i) => {
              const active = i === hoverIdx
              return (
                <div
                  key={i}
                  className={'absolute rounded-full bg-white border-2 border-amber-500 -translate-x-1/2 -translate-y-1/2 transition-transform ' +
                    (active ? 'w-3 h-3 shadow-sm shadow-amber-500/30' : 'w-2 h-2')}
                  style={{ left: `${p.normX * 100}%`, top: `${(p.y / H) * 100}%` }}
                />
              )
            })}

            {hoverIdx != null && pts[hoverIdx] && (
              <div
                className="absolute -translate-x-1/2 -translate-y-full -mt-3 pointer-events-none"
                style={{ left: `${pts[hoverIdx].normX * 100}%`, top: `${(pts[hoverIdx].y / H) * 100}%` }}
              >
                <div className="bg-zinc-900 text-white text-[11px] font-medium px-2 py-1 rounded-md shadow-[0_4px_12px_-4px_rgba(0,0,0,0.4)] whitespace-nowrap tabular-nums">
                  <span className="text-zinc-400 mr-1.5">{fmtMonth(pts[hoverIdx].m.month)}</span>
                  <span>{pts[hoverIdx].m.count.toLocaleString()} follow-ups</span>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && data.length > 0 && (
          <div className="relative mt-1 h-4 text-[10px] text-zinc-400">
            {labelIdx.map((i) => (
              <span key={i}
                className="absolute -translate-x-1/2 whitespace-nowrap"
                style={{
                  left: `${(i / Math.max(1, data.length - 1)) * 100}%`,
                  transform: i === 0 ? 'translateX(0)' : i === data.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                }}>
                {fmtMonth(data[i]?.month)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


/* Small read-only row for the "Other follow-ups" list inside the drawer.
   Same visual language as the existing follow-up rows on the main page. */
function HistoryItem({ f, onClick, disabled }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="w-full flex items-start gap-2 px-2.5 py-1.5 rounded-lg bg-zinc-50/70 ring-1 ring-zinc-100 hover:bg-amber-50/40 hover:ring-amber-200/60 transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        title="Click to open this follow-up"
      >
        <div className="text-[10.5px] tabular-nums text-zinc-500 mt-0.5 shrink-0 w-14 leading-tight">
          {fmtShortDate(f.date)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            {f.channel && (
              <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider font-medium text-zinc-500">
                <span className={`w-1 h-1 rounded-full ${CHANNEL_DOT[f.channel] || 'bg-zinc-400'}`} />
                {f.channel}
              </span>
            )}
            {f.outcome_stage && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ring-1 text-[10px] font-medium ${STAGE_BADGE[f.outcome_stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
                <span className={`w-1 h-1 rounded-full ${STAGE_DOT[f.outcome_stage] || 'bg-zinc-400'}`} />
                {f.outcome_stage}
              </span>
            )}
          </div>
          {f.note && (
            <div className="text-[12px] text-zinc-600 line-clamp-2 leading-snug">{f.note}</div>
          )}
        </div>
      </button>
    </li>
  )
}

function fmtShortDate(u) {
  if (!u) return '—'
  const d = new Date(u * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short' })
}

function unixToInput(u) {
  if (!u) return ''
  const d = new Date(u * 1000)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
function inputToUnix(s) {
  if (!s) return 0
  const t = Date.parse(s)
  return Number.isFinite(t) ? Math.floor(t / 1000) : 0
}
function unixToDate(u) {
  if (!u) return ''
  const d = new Date(u * 1000)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
function dateToUnix(s) {
  if (!s) return 0
  const t = Date.parse(s + 'T00:00:00')
  return Number.isFinite(t) ? Math.floor(t / 1000) : 0
}
function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
